/**
 * Centralised i18n descriptors for `@atlassian/guard-redaction-ui`.
 *
 * Consumers should not redefine these strings — they are shared across all
 * Guard redaction surfaces so a translation change lands in one place.
 */

import { defineMessages, type MessageDescriptor } from 'react-intl';

type GuardRedactionMessageKey =
	| 'redactedByOrganization'
	| 'blockedByOrganization'
	| 'blockedBodyGeneric'
	| 'blockedBodyResponse'
	| 'traceId'
	| 'redactedTokenA11y'
	| 'redactedTooltipLine1'
	| 'redactedTooltipLine2'
	| 'flagTitle'
	| 'flagDescriptionSingular'
	| 'flagDescriptionPlural'
	| 'flagRemoveRedaction'
	| 'flagRemoveRedactions'
	| 'flagLearnMore'
	| 'flagIconLabel'
	| 'flagLocationPageBody'
	| 'flagLocationPageTitle'
	| 'flagLocationComment'
	| 'flagLocationIssueDescription'
	| 'flagLocationIssueSummary'
	| 'flagLocationCustomField';

// Explicit Record type satisfies --isolatedDeclarations.
export const messages: Record<GuardRedactionMessageKey, MessageDescriptor> = defineMessages({
	redactedByOrganization: {
		id: 'guard-redaction-ui.enforcement.redacted',
		defaultMessage: 'Redacted by your organization',
		description: 'Footer shown beneath any content that has been redacted by Atlassian Guard.',
	},
	blockedByOrganization: {
		id: 'guard-redaction-ui.enforcement.blocked',
		defaultMessage: 'Blocked by your organization',
		description: 'Footer shown when content was blocked by Atlassian Guard.',
	},
	blockedBodyGeneric: {
		id: 'guard-redaction-ui.block.body.generic',
		defaultMessage: 'This was blocked by your organization\u2019s data security policy.',
		description: 'Generic body copy for a Guard block (prompt/attachment).',
	},
	blockedBodyResponse: {
		id: 'guard-redaction-ui.block.body.response',
		defaultMessage: 'The response was blocked by your organization\u2019s data security policy.',
		description: 'Body copy for a Guard block on an assistant response.',
	},
	traceId: {
		id: 'guard-redaction-ui.block.trace-id',
		defaultMessage: 'Trace ID: {traceId}',
		description: 'Trace ID shown beneath a Guard block panel for support correlation.',
	},
	redactedTokenA11y: {
		id: 'guard-redaction-ui.scribble.a11y',
		defaultMessage: 'Redacted content',
		description: 'Accessible label for the redacted scribble bar.',
	},
	redactedTooltipLine1: {
		id: 'guard-redaction-ui.scribble.tooltip.line1',
		defaultMessage: 'Redacted by your organization.',
		description: 'First line of the tooltip shown when hovering a redacted scribble bar.',
	},
	redactedTooltipLine2: {
		id: 'guard-redaction-ui.scribble.tooltip.line2',
		defaultMessage: 'Powered by Atlassian Guard.',
		description: 'Second line of the tooltip shown when hovering a redacted scribble bar.',
	},
	flagTitle: {
		id: 'guard-redaction-ui.flag.title',
		defaultMessage: 'Redacted by your organization',
		description:
			'Title of the notification flag shown when Atlassian Guard redacts sensitive data.',
	},
	flagDescriptionSingular: {
		id: 'guard-redaction-ui.flag.description.singular',
		defaultMessage: 'Sensitive information was detected in the {location} and has been redacted.',
		description:
			'Body of the redaction flag when one item was redacted. {location} is where it happened, e.g. "page body" or "comment".',
	},
	flagDescriptionPlural: {
		id: 'guard-redaction-ui.flag.description.plural',
		defaultMessage:
			'{count} sensitive items were detected in the {location} and have been redacted.',
		description:
			'Body of the redaction flag when several items were redacted. {count} is the number of items (2 or more), {location} is where it happened, e.g. "page body".',
	},
	flagRemoveRedaction: {
		id: 'guard-redaction-ui.flag.action.remove-redaction',
		defaultMessage: 'Remove redaction',
		description:
			'Flag action that deletes the single redacted element from the content. It never restores the original text.',
	},
	flagRemoveRedactions: {
		id: 'guard-redaction-ui.flag.action.remove-redactions',
		defaultMessage: 'Remove redactions',
		description:
			'Flag action that deletes all redacted elements from the content. It never restores the original text.',
	},
	flagLearnMore: {
		id: 'guard-redaction-ui.flag.action.learn-more',
		defaultMessage: 'Learn more',
		description: 'Flag action that opens the Atlassian Guard support article about redaction.',
	},
	flagIconLabel: {
		id: 'guard-redaction-ui.flag.icon.label',
		defaultMessage: 'Atlassian Guard',
		description: 'Accessible label of the Atlassian Guard icon shown in the redaction flag.',
	},
	flagLocationPageBody: {
		id: 'guard-redaction-ui.flag.location.page-body',
		defaultMessage: 'page body',
		description:
			'Location inserted into the redaction flag description, e.g. "detected in the page body".',
	},
	flagLocationPageTitle: {
		id: 'guard-redaction-ui.flag.location.page-title',
		defaultMessage: 'page title',
		description:
			'Location inserted into the redaction flag description, e.g. "detected in the page title".',
	},
	flagLocationComment: {
		id: 'guard-redaction-ui.flag.location.comment',
		defaultMessage: 'comment',
		description:
			'Location inserted into the redaction flag description, e.g. "detected in the comment".',
	},
	flagLocationIssueDescription: {
		id: 'guard-redaction-ui.flag.location.issue-description',
		defaultMessage: 'description',
		description:
			'Location inserted into the redaction flag description for a Jira work item description, e.g. "detected in the description".',
	},
	flagLocationIssueSummary: {
		id: 'guard-redaction-ui.flag.location.issue-summary',
		defaultMessage: 'summary',
		description:
			'Location inserted into the redaction flag description for a Jira work item summary, e.g. "detected in the summary".',
	},
	flagLocationCustomField: {
		id: 'guard-redaction-ui.flag.location.custom-field',
		defaultMessage: 'custom field',
		description:
			'Location inserted into the redaction flag description for a Jira custom field, e.g. "detected in the custom field".',
	},
});
